import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import useLayoutSize from '@/hooks/useLayoutSize';
import {
  SETTLEMENT_BUTTON_ID,
  SETTLEMENT_CONTENT_ID,
  SETTLEMENT_MODAL_ID,
  SETTLEMENT_TITLE_ID,
} from './content';
import {
  SerpSeasonEndDetailRoot,
  SerpSeasonEndRoot,
  SerpSeasonEndSheetBody,
} from './styles';

export { buildSerpSeasonEndContentHtml } from './content';

type SettlementModalPart = { title: string; body: string };

/** 저장 content 의 정산내역 모달 틀 → 제목·내용 HTML */
const readSettlementModal = (content: string): SettlementModalPart => {
  const doc = new DOMParser().parseFromString(content, 'text/html');
  return {
    title: doc.getElementById(SETTLEMENT_TITLE_ID)?.textContent?.trim() ?? '',
    body: doc.getElementById(SETTLEMENT_CONTENT_ID)?.innerHTML ?? '',
  };
};

type SettlementDialogProps = SettlementModalPart & {
  open: boolean;
  onClose: () => void;
};

/** 「정산내역보기」 — PC 모달, 모바일 바텀시트 */
const SettlementDialog = ({
  open,
  onClose,
  title,
  body,
}: SettlementDialogProps) => {
  const { t } = useTranslation();
  const { isMobile } = useLayoutSize();
  const modalRef = useRef<ModalRef>(null);
  const dialogTitle = title || t('정산내역');

  useEffect(() => {
    if (isMobile) return;
    if (open) modalRef.current?.open();
    else modalRef.current?.close();
  }, [open, isMobile]);

  const content = (
    <SerpSeasonEndDetailRoot>
      <SerpContentSnapshot content={body} />
    </SerpSeasonEndDetailRoot>
  );

  if (isMobile) {
    return (
      <MobileBottomSheet
        open={open}
        onClose={onClose}
        title={dialogTitle}
        height="auto"
        maxHeight="80vh"
        swipeable={false}
      >
        <SerpSeasonEndSheetBody>{content}</SerpSeasonEndSheetBody>
      </MobileBottomSheet>
    );
  }
  return (
    <Modal ref={modalRef} title={dialogTitle} handleClose={onClose}>
      {content}
    </Modal>
  );
};

/** 저장 content(레거시 문서 포함) 그대로 — 「정산내역보기」는 저장된 모달 내용을 포탈 모달로 */
export const SerpSeasonEndSnapshot = ({ content }: { content: string }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const modal = useMemo(() => readSettlementModal(content), [content]);

  const transform = useCallback<ElementTransform>(
    ({ element, props }) => {
      if (element.id === SETTLEMENT_MODAL_ID) return null;
      if (element.id === SETTLEMENT_BUTTON_ID) {
        return (
          <BoxButton
            key={props.key as string}
            type="button"
            variant="secondary"
            size="small"
            onClick={() => setOpen(true)}
          >
            {t('정산내역보기')}
          </BoxButton>
        );
      }
      return undefined;
    },
    [t],
  );

  return (
    <SerpSeasonEndRoot>
      <SerpContentSnapshot content={content} transform={transform} />
      <SettlementDialog
        open={open}
        onClose={() => setOpen(false)}
        title={modal.title}
        body={modal.body}
      />
    </SerpSeasonEndRoot>
  );
};
